import { Box } from "@mui/material";
import { Chip, Link, Typography } from "@platform/design-system";

import { useGoldenClient } from "../api/GoldenClientProvider";
import { openCaseDocument } from "../api/caseDocuments";

import type { EvidenceOut } from "@platform/api/golden";
import type { ReactNode } from "react";

export const EVIDENCE_LIST_TEST_ID = "golden-evidence";

interface EvidenceProps {
  readonly caseId: number;
  readonly onError: (message: string) => void;
}

// Entries carry no id; what one says and where it points tells it from the others.
const evidenceKey = (entry: EvidenceOut): string =>
  [
    entry.source,
    entry.field_path,
    entry.document_id,
    entry.page_number,
    entry.comment,
  ].join("|");

const sourceText = (pageNumber: number | null, documentName: string): string =>
  pageNumber === null ? documentName : `Page ${pageNumber} of ${documentName}`;

/** The page an entry cites, as a link that opens the document there; the document alone when no page was cited. */
const EvidenceSource = ({
  caseId,
  evidence,
  onError,
}: EvidenceProps & { readonly evidence: EvidenceOut }): ReactNode => {
  const client = useGoldenClient();
  const pageNumber = evidence.page_number ?? null;
  const documentId = evidence.document_id ?? null;
  const documentName = evidence.document_name ?? null;

  if (documentId === null || documentName === null) {
    return pageNumber === null ? null : (
      <Typography variant="body-medium" color="textSecondary" component="span">
        Page {pageNumber}, document not named
      </Typography>
    );
  }

  const open = async (): Promise<void> => {
    try {
      await openCaseDocument(
        client,
        caseId,
        { id: documentId, fileName: documentName },
        pageNumber,
      );
    } catch (error: unknown) {
      onError(
        error instanceof Error
          ? error.message
          : "The document could not be opened",
      );
    }
  };

  return (
    <Link component="button" type="button" onClick={() => void open()}>
      {sourceText(pageNumber, documentName)}
    </Link>
  );
};

/**
 * RUMI's evidence for a field: the citations it wrote, each the document's wording
 * behind a value with the page it is on, then its field comments, its reasons. The
 * wording and the reason help find the evidence; the document is the evidence.
 */
export const EvidenceList = ({
  caseId,
  evidence,
  onError,
}: EvidenceProps & {
  readonly evidence: readonly EvidenceOut[];
}): ReactNode => {
  if (evidence.length === 0) {
    return (
      <Typography
        data-testid={`${EVIDENCE_LIST_TEST_ID}-none`}
        variant="body-medium"
        color="textSecondary"
      >
        RUMI left no citation or comment on this field.
      </Typography>
    );
  }

  return (
    <Box
      component="ul"
      data-testid={EVIDENCE_LIST_TEST_ID}
      sx={{ m: 0, pl: 0, listStyle: "none", display: "grid", gap: 1.5 }}
    >
      {evidence.map((entry) => (
        <li key={evidenceKey(entry)}>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Chip
              size="small"
              variant="subtle"
              leadingVisual={false}
              label={entry.source === "citation" ? "Cited" : "Comment"}
            />
            <Box component="span" sx={{ fontFamily: "monospace" }}>
              {entry.field_path}
            </Box>
            {entry.value != null && (
              <Typography
                variant="body-medium"
                color="textSecondary"
                component="span"
              >
                wrote {entry.value}
              </Typography>
            )}
            {entry.needs_review && (
              <Chip
                size="small"
                variant="subtle"
                color="attention"
                leadingVisual={false}
                label="RUMI asked for a review"
              />
            )}
            <EvidenceSource
              caseId={caseId}
              evidence={entry}
              onError={onError}
            />
          </Box>
          <Typography variant="body-medium">
            {entry.source === "citation" ? `“${entry.comment}”` : entry.comment}
          </Typography>
        </li>
      ))}
    </Box>
  );
};
